import { History, Pencil } from 'lucide-react';
import { useState } from 'react';

import type { AlunoOutputType, MateriaOutputType, PainelDadosOutputType } from '@shared/dto';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { horariosTexto } from '../../../components/common/dias-semana';
import { corDaMateria } from '../../../components/common/materia-cores';
import { SITUACAO_ALUNO_LABEL } from '../../../components/common/aluno-form/enum-labels';
import { AlunoFormDialog } from '../../../components/common/aluno-form/aluno-form-dialog';
import { usePainelSnapshot } from '../../../hooks/use-painel-snapshot';

export function AlunoCard({
  aluno,
  materias,
  matriculas,
  onAtualizado,
  onPainelAtualizado,
  onVerHistorico,
}: {
  aluno: AlunoOutputType;
  materias: MateriaOutputType[];
  matriculas: PainelDadosOutputType['matriculas'];
  onAtualizado: () => void;
  onPainelAtualizado?: () => void;
  onVerHistorico: (aluno: { id: string; nome: string }) => void;
}) {
  const [editando, setEditando] = useState(false);
  const { dados } = usePainelSnapshot();
  const professores = dados?.professores ?? [];

  return (
    <Card className="gap-0 rounded-2xl p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-medium">
            <span className="truncate">{aluno.nome}</span>
            {aluno.connect ? (
              <span
                className="flex size-4 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white"
                title="Connect"
              >
                C
              </span>
            ) : null}
          </p>
          <p className="truncate text-sm text-muted-foreground">{aluno.responsavel || '—'}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-md text-xs"
            title="Histórico de acompanhamento"
            onClick={() => onVerHistorico({ id: aluno.id, nome: aluno.nome })}
          >
            <History className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-md text-xs"
            onClick={() => setEditando(true)}
          >
            <Pencil className="size-4" />
          </Button>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {matriculas.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sem matrículas.</p>
        ) : (
          matriculas.map((matricula) => {
            const materiaNome = materias.find((m) => m.id === matricula.materiaId)?.nome ?? '—';
            const professorNome =
              professores.find((p) => p.id === matricula.professorId)?.nome ?? '—';
            return (
              <div key={matricula.id} className="rounded-lg bg-muted/50 px-2.5 py-1.5">
                <div className="flex items-center gap-1.5 text-xs">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: corDaMateria(matricula.materiaId, materias) }}
                  />
                  <span className="font-medium">{materiaNome}</span>
                  {matricula.estagio ? (
                    <span className="text-muted-foreground">· {matricula.estagio}</span>
                  ) : null}
                  <span className="ml-auto truncate text-muted-foreground">{professorNome}</span>
                </div>
                {matricula.horarios.length > 0 ? (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {horariosTexto(matricula.horarios)}
                  </p>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {aluno.situacao !== 'ATIVO' || aluno.zonaVermelha ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {aluno.situacao !== 'ATIVO' ? (
            <Badge variant="secondary">{SITUACAO_ALUNO_LABEL[aluno.situacao]}</Badge>
          ) : null}
          {aluno.zonaVermelha ? <Badge variant="destructive">Zona Vermelha</Badge> : null}
        </div>
      ) : null}

      <AlunoFormDialog
        open={editando}
        onOpenChange={setEditando}
        aluno={aluno}
        materias={materias}
        onSalvo={onAtualizado}
        onPainelAtualizado={onPainelAtualizado}
      />
    </Card>
  );
}
